import React, { useMemo } from 'react';
import { DynamicGridRowType } from '../dynamic-grid/dynamic-grid-builder';

export const GridLabelCharacterLength = {
    rowLabel: 1000,
    columnLabel: 1000,
    cornerPlaceholder: 1000,
};

function isBlankLabel(value: string | undefined | null): boolean {
    return (value ?? '').trim().length === 0;
}

function normalizeText(value: string | undefined | null): string {
    return (value ?? '').trim().toLowerCase();
}

function findDuplicates(values: string[]): string[] {
    const seen = new Set<string>();
    const duplicates = new Set<string>();

    for (const value of values) {
        if (!value) {
            continue;
        }
        if (seen.has(value)) {
            duplicates.add(value);
        } else {
            seen.add(value);
        }
    }

    return Array.from(duplicates);
}

export function useDynamicGridValidation(grid: DynamicGridRowType[] | undefined) {
    return useMemo(() => {
        const errors: string[] = [];

        if (!grid?.length || !grid[0]?.columns?.length) {
            return { isValid: false, errors: ['Grid is missing rows/columns.'] };
        }

        const hasBlankRowLabel = grid.some((row) => isBlankLabel(row.rowLabel));
        if (hasBlankRowLabel) {
            errors.push('Row labels cannot be blank.');
        }

        const headerCols = grid[0].columns ?? [];
        const hasBlankColumnLabel = headerCols.some((column) => isBlankLabel(column.columnLabel));
        if (hasBlankColumnLabel) {
            errors.push('Column labels cannot be blank.');
        }

        const rowLabelsNormalized = grid.map((row) => normalizeText(row.rowLabel));
        if (findDuplicates(rowLabelsNormalized).length > 0) {
            errors.push('Row labels must be unique.');
        }

        const columnLabelsNormalized = headerCols.map((column) => normalizeText(column.columnLabel));
        if (findDuplicates(columnLabelsNormalized).length > 0) {
            errors.push('Column labels must be unique.');
        }
        const canonicalByOrder = new Map<number, string>();
        for (const column of headerCols) {
            canonicalByOrder.set(column.columnOrder, normalizeText(column.columnLabel));
        }

        return { isValid: errors.length === 0, errors };
    }, [grid]);
}
